import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {cognitoOptions} from '../../../cognito-options';
import {GenericOAuth2} from '@capacitor-community/generic-oauth2';
import {assert, is} from 'typia';
import {StorageService} from '../storage.service';
import {environment} from '../../../../environments/environment';
import {OAuthState, Tokens} from "./auth.service";
import {isNative} from "../../utils";

export type FederatedAuthPlatform = 'google' | 'apple';

export interface FederatedAuthTokens {
  source: FederatedAuthPlatform;
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}


interface OldRefreshResponse {
  access_token_response: {
    id_token: string;
    access_token: string;
    refresh_token: string;
    expires_in?: number;
    expires_at?: number
  }
}

interface NewRefreshResponse {
  access_token: string;
  id_token: string;
  expires_in?: number;
  expires_at?: number
}

// RFC 6749 §5.2 token error response. If the server returned one of these
// error codes the refresh will fail the same way on retry, so we rethrow
// instead of burning a retry. Anything else (network blip, plugin/transport
// failure) falls through to the retry path.
type OAuthTokenErrorCode =
  | 'invalid_request'
  | 'invalid_client'
  | 'invalid_grant'
  | 'unauthorized_client'
  | 'unsupported_grant_type'
  | 'invalid_scope';

interface OAuthServerResponseError {
  readonly error: OAuthTokenErrorCode;
}

const identityProviderMap: Record<FederatedAuthPlatform, string> = {
  google: 'Google',
  apple: 'SignInWithApple',
}

@Injectable({providedIn: 'root'})
export class FederatedAuthService {
  private readonly storageService = inject(StorageService);
  private readonly router = inject(Router);
  private refreshPromise: Promise<Tokens | undefined> | undefined;

  /**
   * Initiates federated login.
   * On native platforms uses Capacitor plugin; on web does standard redirect.
   */
  public async federatedLogin(
    platform: FederatedAuthPlatform,
    oauthState: OAuthState,
  ): Promise<'success' | 'cancelled'> {
    const state = crypto.randomUUID();

    if (isNative) {
      // Native: use Capacitor OAuth2 plugin
      try {
        const result = await GenericOAuth2.authenticate({
          ...cognitoOptions,
          additionalParameters: {
            state,
            identity_provider: identityProviderMap[platform],
          }
        });

        const parsed = assert<{
          access_token_response: {
            id_token: string;
            access_token: string;
            refresh_token: string;
            expires_in?: number;
            expires_at?: number
          }
        }>(result);

        const resp = parsed.access_token_response;
        const expiresAt = resp.expires_at
          ? resp.expires_at
          : Date.now() + (resp.expires_in! * 1000);

        const tokens: FederatedAuthTokens = {
          source: platform,
          idToken: resp.id_token,
          accessToken: resp.access_token,
          refreshToken: resp.refresh_token,
          expiresAt
        };

        await this.storageService.setItem('AUTH_TOKENS', tokens);
        return 'success';
      } catch (err: any) {
        // if the user pressed “back” / closed the window, plugin rejects with USER_CANCELLED
        if (
          err === 'USER_CANCELLED' ||
          err.error === 'USER_CANCELLED' ||
          err.errorMessage === 'USER_CANCELLED' ||
          err.message === 'USER_CANCELLED'
        ) {
          // 🔇 hide it entirely
          return 'cancelled';
        }
        // otherwise handle or display the real error

        console.error('Error during native federated login', err);
        throw err;
      }
    } else {
      await this.storageService.setItem(
        'OATH_STATE',
        oauthState
      )
      const params = new URLSearchParams({
        response_type: 'code',
        client_id: cognitoOptions.appId,
        redirect_uri: cognitoOptions.web!.redirectUrl!,
        state,
        identity_provider: identityProviderMap[platform],
      });
      const url = `https://${environment.cognitoOAuthDomain}/oauth2/authorize?${params}`;
      if (typeof window === 'undefined') {
        throw new Error('Federated login requires a browser environment');
      }
      window.location.href = url;
      return new Promise<'success'>(() => {
      });
    }
  }

  /**
   * Handles the OAuth2 callback on web: exchanges code for tokens.
   * Call this from your OAuthCallbackComponent.
   */
  public async handleWebCallback(
    code: string
  ): Promise<OAuthState | undefined> {
    // Exchange code for tokens
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: cognitoOptions.appId,
      redirect_uri: cognitoOptions.web!.redirectUrl!,
      code
    });

    const resp = await fetch(
      `https://${environment.cognitoOAuthDomain}/oauth2/token/`,
      {
        method: 'POST',
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        body: body.toString()
      }
    );
    if (!resp.ok) {
      throw new Error(`Token exchange failed: ${resp.status}`);
    }

    const data = await resp.json();
    const expiresAt = Date.now() + data.expires_in * 1000;

    const tokens: FederatedAuthTokens = {
      source: 'google', // Default to Google, can be changed based on your logic
      idToken: data.id_token,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresAt
    };
    await this.storageService.setItem('AUTH_TOKENS', tokens);
    const state = await this.storageService.getItem('OATH_STATE');
    await this.storageService.removeItem('OATH_STATE');
    return state;
  }

  public async getTokens(): Promise<Tokens | undefined> {
    const tokens = await this.storageService.getItem('AUTH_TOKENS');
    if (!tokens) return undefined;

    if (tokens.expiresAt < Date.now()) {
      if (!this.refreshPromise) {
        this.refreshPromise = this.performRefresh(tokens)
          .finally(() => this.refreshPromise = undefined);
      }
      return this.refreshPromise;
    }

    return {
      source: tokens.source,
      idToken: tokens.idToken,
      accessToken: tokens.accessToken,
    };
  }

  private async performRefresh(tokens: FederatedAuthTokens): Promise<Tokens | undefined> {
    if (isNative) {
      return this.refreshNativeTokens(tokens);
    } else {
      return this.refreshWebTokens(tokens);
    }
  }

  private async refreshNativeTokens(tokens: FederatedAuthTokens): Promise<Tokens | undefined> {
    try {
      const response = await this.refreshNativeTokensWithRetry(tokens.refreshToken);

      if (is<OldRefreshResponse | NewRefreshResponse>(response)) {
        let newTokens: FederatedAuthTokens;

        if (is<OldRefreshResponse>(response)) {
          const resp = response.access_token_response;
          const expiresAt = resp.expires_at
            ? resp.expires_at
            : Date.now() + (resp.expires_in! * 1000);

          newTokens = {
            source: tokens.source,
            idToken: resp.id_token,
            accessToken: resp.access_token,
            refreshToken: resp.refresh_token,
            expiresAt
          };
        } else {
          const expiresAt = response.expires_at
            ? response.expires_at
            : Date.now() + (response.expires_in! * 1000);

          newTokens = {
            source: tokens.source,
            idToken: response.id_token,
            accessToken: response.access_token,
            refreshToken: tokens.refreshToken,
            expiresAt
          };
        }

        await this.storageService.setItem('AUTH_TOKENS', newTokens);
        return newTokens;
      } else {
        console.error("Unknown token refresh response format", JSON.stringify(response));
        throw new Error("Token refresh failed");
      }
    } catch (err) {
      console.warn(`Error refreshing native token (access token expired at ${new Date(tokens.expiresAt).toISOString()})`, err);
      await this.storageService.removeItem('AUTH_TOKENS');
      return undefined;
    }
  }

  private async refreshNativeTokensWithRetry(refreshToken: string): Promise<unknown> {
    try {
      return await GenericOAuth2.refreshToken({
        ...cognitoOptions,
        refreshToken,
      });
    } catch (err) {
      if (is<OAuthServerResponseError>(err)) {
        throw err;
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
      return await GenericOAuth2.refreshToken({
        ...cognitoOptions,
        refreshToken,
      });
    }
  }

  private async refreshWebTokens(tokens: FederatedAuthTokens): Promise<Tokens | undefined> {
    try {
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: cognitoOptions.appId,
        refresh_token: tokens.refreshToken,
      });
      const resp = await fetch(`https://${environment.cognitoOAuthDomain}/oauth2/token`, {
        method: 'POST',
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        body: body.toString()
      });
      if (!resp.ok) {
        throw new Error(`Refresh failed: ${resp.status}`);
      }
      const data = await resp.json();
      const expiresAt = Date.now() + data.expires_in * 1000;
      const refreshed: FederatedAuthTokens = {
        source: tokens.source,
        idToken: data.id_token,
        accessToken: data.access_token,
        refreshToken: data.refresh_token || tokens.refreshToken,
        expiresAt
      };
      await this.storageService.setItem('AUTH_TOKENS', refreshed);
      return refreshed;
    } catch (err) {
      console.warn(`Error refreshing web token (access token expired at ${new Date(tokens.expiresAt).toISOString()})`, err);
      await this.storageService.removeItem('AUTH_TOKENS');
      return undefined;
    }
  }

  /**
   * Logs out user: clears storage and redirects.
   */
  public async logout(): Promise<void> {
    await this.storageService.removeItem('AUTH_TOKENS');
    const logoutUrl =
      `https://${environment.cognitoOAuthDomain}/logout?` +
      `client_id=${cognitoOptions.appId}&` +
      `response_type=code&` +
      `logout_uri=${encodeURIComponent(environment.baseUrl)}&` +
      `redirect_uri=${encodeURIComponent(cognitoOptions.web.redirectUrl)}`;

    if (isNative) {
      await GenericOAuth2.logout({logoutUrl});
    } else {
      if (typeof window === 'undefined') {
        throw new Error('Federated logout requires a browser environment');
      }
      window.location.href = logoutUrl;
      return new Promise<void>(() => {
      });
    }
  }
}
